import {
  DataVersion,
  IncidentDetail,
  IncidentProduct,
} from '@hakimo-ui/hakimo/types';
import { CamerasCard, EditableCard } from './editable-fields';
import { IncidentTimelineSection } from './IncidentTimelineSection';
import {
  IncidentSummaryPanel,
  NeighbouringAlarmsPanel,
  SopPanel,
} from './panels';
import { EditableField } from './util';

interface NeighbourAlarm {
  id: number | string;
  alarm_start_time_utc?: string | null;
  timezone?: string | null;
  alarm_start_time?: string | null;
  is_escalated: boolean;
}

interface Props {
  incident: IncidentDetail;
  product: IncidentProduct;
  neighbours: NeighbourAlarm[];
  neighboursLoading: boolean;
  saving: boolean;
  onSaveField: (
    field: EditableField,
    value: string | string[],
    onSaved: () => void
  ) => void;
  onTimelineChanged: () => void;
  dataVersion?: DataVersion;
}

export function IncidentDetailsBody({
  incident,
  product,
  neighbours,
  neighboursLoading,
  saving,
  onSaveField,
  onTimelineChanged,
  dataVersion,
}: Props) {
  return (
    <div className="grid grid-cols-1 gap-2 py-2 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
      <div className="space-y-2">
        <IncidentSummaryPanel incident={incident} />
        <NeighbouringAlarmsPanel
          alarms={neighbours}
          current={{
            id: incident.alarm_id,
            // v2 fills alarm_start_time_utc + timezone; v3's
            // location_incidents_v3 fills alarm_start_time instead (see
            // IncidentDetail's field comments). Pass both through --
            // NeighbouringAlarmsPanel picks whichever is present.
            alarm_start_time_utc: incident.alarm_start_time_utc,
            timezone: incident.timezone,
            alarm_start_time: incident.alarm_start_time,
            is_escalated: true,
          }}
          product={product}
          loading={neighboursLoading}
          dataVersion={dataVersion}
        />
        <SopPanel locationId={incident.location_id} />
      </div>

      <div className="space-y-2">
        <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
          <EditableCard
            title="Description"
            value={incident.description}
            originalValue={incident.original_description}
            placeholder="Describe the incident"
            saving={saving}
            onSave={(next, onSaved) =>
              onSaveField('reported_description', next, onSaved)
            }
          />
          <EditableCard
            title="Resolution Comment"
            value={incident.resolution_comment}
            originalValue={incident.original_resolution_comment}
            placeholder="How was the incident resolved?"
            saving={saving}
            onSave={(next, onSaved) =>
              onSaveField('reported_resolution_comment', next, onSaved)
            }
          />
        </div>

        <CamerasCard
          cameras={incident.cameras}
          originalCameras={incident.original_cameras}
          saving={saving}
          onSave={(next, onSaved) =>
            onSaveField('reported_cameras', next, onSaved)
          }
        />

        <IncidentTimelineSection
          alarmId={incident.alarm_id}
          product={product}
          timezone={incident.timezone}
          alarmStartTimeUtc={incident.alarm_start_time_utc}
          dataVersion={dataVersion}
          onTimelineChanged={onTimelineChanged}
        />
      </div>
    </div>
  );
}

export default IncidentDetailsBody;
